<template>
  <div v-if="canView">
    <v-tooltip v-if="button" top>
      <v-btn
        slot="activator"
        :icon="icon && amount <= 0"
        :round="!icon"
        small
        :flat="incentive.amount <= 0"
        :color="incentive.amount > 0 && canEdit ? 'primary' : ''"
        :class="incentive.amount > 0 ? 'black--text incentive-action' : ''"
        @click="open"
      >
        <v-icon :left="!icon" small>{{ isEdit ? "edit" : "stars" }}</v-icon>
        <span v-if="isEdit">Edit </span>
        <span v-else-if="!icon"> Incentive {{ formatAmount(amount) }} ฿ </span>
        <span v-else-if="amount > 0" class="incentive-compact-amount">
          <s v-if="hasAdjustment">{{ formatAmount(previousAmount) }}</s>
          {{ formatAmount(amount) }} ฿
        </span>
      </v-btn>
      <span>
        {{
          amount > 0
            ? ` Incentive ${formatAmount(amount)} บาท`
            : "เพิ่ม  Incentive"
        }}
      </span>
    </v-tooltip>

    <v-card v-else flat class="incentive-summary glass-section">
      <v-card-text class="incentive-summary__body">
        <div class="incentive-summary__icon">
          <v-icon>stars</v-icon>
        </div>
        <div class="incentive-summary__copy">
          <div class="incentive-summary__eyebrow">Rider Incentive</div>
          <div class="incentive-summary__amount">
            <s v-if="hasAdjustment" class="incentive-summary__previous">
              {{ formatAmount(previousAmount) }} บาท
            </s>
            {{ formatAmount(amount) }} บาท
          </div>
          <div v-if="lastAdjustment" class="adjustment-meta">
            แก้ไขโดย {{ adjustmentActor(lastAdjustment) }} ·
            {{ formatDateTime(lastAdjustment.created_at) }}
          </div>
        </div>
        <v-chip
          v-if="incentive.status"
          small
          :color="statusColor(incentive.status)"
          text-color="white"
        >
          {{ statusLabel(incentive.status) }}
        </v-chip>
        <v-spacer />
        <v-btn
          v-if="canEdit"
          round
          color="primary"
          class="incentive-action black--text"
          @click="open"
        >
          <v-icon left small>edit</v-icon>
          แก้ไข
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="620"
      content-class="incentive-dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="incentive-dialog-card">
        <v-toolbar dark color="rgba(24, 28, 36, .94)" class="dialog-toolbar">
          <v-toolbar-title>Incentive</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="incentive-dialog-content">
          <section class="dialog-order-card">
            <div class="dialog-order-card__icon">
              <v-icon>receipt</v-icon>
            </div>
            <div>
              <div class="dialog-label">ORDER</div>
              <div class="dialog-order-code">#{{ orderCode || orderId }}</div>
              <div class="dialog-caption">
                เงินรางวัลเพิ่มเติมสำหรับ Rider เมื่องานสำเร็จ
              </div>
            </div>
          </section>

          <section class="dialog-form-card">
            <div class="dialog-section-title">Incentive setup</div>
            <div class="dialog-caption mb-3">
              ระบุจำนวนเต็มเป็นบาท โดยใส่ 0 เมื่อต้องการยกเลิก
            </div>
            <v-text-field
              v-model="inputAmount"
              type="number"
              min="0"
              step="1"
              suffix="บาท"
              label="จำนวนเงิน Incentive"
              outline
              prepend-inner-icon="stars"
              :disabled="!canEdit || saving"
              hint="Rider จะเห็นรายการนี้ในชื่อ “โบนัส”"
              persistent-hint
            />

            <v-alert v-if="error" type="error" :value="true" class="mt-3">
              {{ error }}
            </v-alert>
          </section>

          <section
            v-if="adjustments.length"
            class="dialog-form-card audit-card"
          >
            <div class="dialog-section-title">Adjustment history</div>
            <div
              v-for="adjustment in adjustments"
              :key="adjustment.id"
              class="audit-row"
            >
              <div>
                <s>{{ formatAmount(adjustment.old_amount) }} บาท</s>
                <v-icon small>arrow_forward</v-icon>
                <strong>{{ formatAmount(adjustment.new_amount) }} บาท</strong>
              </div>
              <div class="dialog-caption">
                {{ adjustmentActor(adjustment) }} ·
                {{ formatDateTime(adjustment.created_at) }}
              </div>
            </div>
          </section>

          <section
            v-if="approvalEvents.length"
            class="dialog-form-card audit-card"
          >
            <div class="dialog-section-title">Approval audit</div>
            <div
              v-for="event in approvalEvents"
              :key="`approval-${event.id}`"
              class="audit-row"
            >
              <div>
                <strong>{{ approvalEventLabel(event.event_type) }}</strong>
              </div>
              <div class="dialog-caption">
                {{
                  event.actor_user_name || `User ${event.actor_user_id || "-"}`
                }}
                ·
                {{ formatDateTime(event.created_at) }}
              </div>
            </div>
          </section>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn round flat @click="dialog = false">ปิด</v-btn>
          <v-btn
            v-if="canCancel && canEdit && amount > 0"
            round
            flat
            color="error"
            :loading="saving"
            @click="cancelIncentive"
          >
            <v-icon left small>cancel</v-icon>
            ยกเลิก Incentive
          </v-btn>
          <v-btn
            v-if="canEdit"
            round
            color="primary"
            class="black--text"
            :loading="saving"
            @click="save"
          >
            <v-icon left small>save</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { globalAxios } from "@/axios-path.js";

const EDITOR_ROLES = [
  "superadmin",
  "root",
  "callcenter",
  "callcenteradmin",
  "accounting",
];
const APPROVER_ROLES = ["accountant", "accounting", "superadmin", "root"];

export default {
  props: {
    orderId: { required: true },
    orderCode: { default: "" },
    province: { required: true },
    value: { default: () => ({ amount: 0 }) },
    orderStatus: { default: null },
    button: { type: Boolean, default: true },
    icon: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    canCancel: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      saving: false,
      inputAmount: "0",
      error: "",
      incentive: this.value || { amount: 0 },
    };
  },
  computed: {
    amount() {
      return Number((this.incentive && this.incentive.amount) || 0);
    },
    normalizedRole() {
      return String(this.$store.state.role || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    },
    hasEditorRole() {
      return EDITOR_ROLES.includes(this.normalizedRole);
    },
    hasApproverRole() {
      return APPROVER_ROLES.includes(this.normalizedRole);
    },
    canEdit() {
      if (Number(this.orderStatus) === 7) return false;
      if (Number(this.orderStatus) === 6) {
        return (
          this.hasApproverRole && this.incentive.status === "pending_approval"
        );
      }
      return this.hasEditorRole;
    },
    canView() {
      return (
        this.hasEditorRole ||
        this.hasApproverRole ||
        this.amount > 0 ||
        !this.button
      );
    },
    lastAdjustment() {
      return (this.incentive && this.incentive.last_adjustment) || null;
    },
    previousAmount() {
      return this.lastAdjustment
        ? Number(this.lastAdjustment.old_amount || 0)
        : null;
    },
    hasAdjustment() {
      return (
        this.previousAmount !== null && this.previousAmount !== this.amount
      );
    },
    adjustments() {
      const items = (this.incentive && this.incentive.adjustments) || [];
      return items.slice().reverse();
    },
    approvalEvents() {
      const items = (this.incentive && this.incentive.approval_events) || [];
      return items.slice().reverse();
    },
  },
  watch: {
    value(value) {
      this.incentive = value || { amount: 0 };
    },
  },
  methods: {
    formatAmount(value) {
      return Number(value || 0).toLocaleString("th-TH");
    },
    statusColor(status) {
      return (
        {
          pending: "blue-grey",
          pending_approval: "warning",
          processing: "info",
          paid: "success",
          cancelled: "error",
        }[status] || "grey"
      );
    },
    statusLabel(status) {
      return (
        {
          pending: "รอจบงาน",
          pending_approval: "รออนุมัติ",
          processing: "กำลังจ่าย",
          paid: "จ่ายแล้ว",
          cancelled: "ยกเลิก",
        }[status] || status
      );
    },
    async open() {
      this.error = "";
      this.inputAmount = String(this.amount);
      this.dialog = true;
      try {
        const response = await globalAxios.get(
          `/api/v1/order-incentives/${this.province}/${this.orderId}`
        );
        this.incentive = response.data;
        this.inputAmount = String(this.amount);
      } catch (error) {
        this.error =
          error.response?.data?.detail || "ไม่สามารถโหลดประวัติ Incentive ได้";
      }
    },
    adjustmentActor(adjustment) {
      return adjustment.actor_user_name || `User ${adjustment.actor_user_id}`;
    },
    approvalEventLabel(eventType) {
      return (
        {
          approval_attempt: "เริ่มอนุมัติ",
          payout_failed: "จ่ายไม่สำเร็จ",
          paid: "อนุมัติและจ่ายแล้ว",
        }[eventType] || eventType
      );
    },
    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("th-TH");
    },
    applySavedIncentive(incentive) {
      this.incentive = incentive;
      this.$store.commit("fetchOrderListBack/updateOrderIncentive", {
        orderId: this.orderId,
        incentive,
      });
      this.$emit("input", incentive);
      this.$emit("saved", incentive);
    },
    async save() {
      const amount = Number(this.inputAmount);
      if (!Number.isInteger(amount) || amount < 0) {
        this.error = "กรุณาระบุจำนวนเต็มตั้งแต่ 0 บาทขึ้นไป";
        return;
      }
      this.saving = true;
      this.error = "";
      try {
        const response = await globalAxios.put(
          `/api/v1/order-incentives/${this.province}/${this.orderId}`,
          { amount }
        );
        this.applySavedIncentive(response.data);
        this.dialog = false;
      } catch (error) {
        this.error =
          error.response?.data?.detail || "ไม่สามารถบันทึก Order Incentive ได้";
      } finally {
        this.saving = false;
      }
    },
    async cancelIncentive() {
      if (!window.confirm("ยืนยันการยกเลิก Incentive รายการนี้?")) return;
      this.saving = true;
      this.error = "";
      try {
        const response = await globalAxios.delete(
          `/api/v1/order-incentives/${this.province}/${this.orderId}`
        );
        this.applySavedIncentive(response.data);
        this.dialog = false;
      } catch (error) {
        this.error =
          error.response?.data?.detail || "ไม่สามารถยกเลิก Incentive ได้";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.glass-section {
  background: rgba(255, 255, 255, 0.68) !important;
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  border-radius: 16px;
  box-shadow: 0 18px 52px rgba(27, 39, 51, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.incentive-action {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 8px 18px rgba(253, 223, 0, 0.38) !important;
}

.incentive-summary {
  margin: 12px 0;
  overflow: hidden;
}

.incentive-summary__body {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  min-height: 96px;
}

.incentive-summary__icon,
.dialog-order-card__icon {
  align-items: center;
  background: rgba(253, 223, 0, 0.2);
  border: 1px solid rgba(214, 185, 0, 0.2);
  border-radius: 16px;
  display: flex;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.incentive-summary__icon .v-icon,
.dialog-order-card__icon .v-icon {
  color: #b99f00;
  font-size: 30px;
}

.incentive-summary__copy {
  min-width: 170px;
}

.incentive-summary__eyebrow,
.dialog-label {
  color: #9b8500;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.incentive-summary__title {
  color: #263238;
  font-size: 18px;
  font-weight: 700;
}

.incentive-summary__amount {
  color: #1b5e20;
  font-size: 24px;
  font-weight: 750;
}

.incentive-summary__previous {
  color: #8b9297;
  font-size: 15px;
  font-weight: 500;
  margin-right: 8px;
}

.incentive-compact-amount {
  align-items: center;
  display: inline-flex;
  gap: 5px;
  margin-left: 3px;
}

.incentive-compact-amount s {
  color: #777777;
  font-size: 11px;
}

.adjustment-meta {
  color: #737d84;
  font-size: 11px;
  margin-top: 3px;
}

.incentive-dialog-card {
  background: rgba(244, 247, 248, 0.94) !important;
  border: 1px solid rgba(255, 255, 255, 0.78) !important;
  border-radius: 20px !important;
}

.dialog-toolbar {
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.incentive-dialog-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.dialog-order-card,
.dialog-form-card {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(210, 217, 222, 0.9);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.06);
  padding: 18px;
}

.audit-card {
  margin-top: 16px;
  padding-bottom: 18px;
}

.audit-row {
  border-top: 1px solid #e5e9eb;
  padding: 10px 0;
}

.audit-row:first-of-type {
  margin-top: 8px;
}

.dialog-order-card {
  align-items: center;
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.dialog-order-code {
  color: #263238;
  font-size: 20px;
  font-weight: 750;
}

.dialog-form-card {
  padding-bottom: 10px;
}

.dialog-section-title {
  color: #263238;
  font-size: 18px;
  font-weight: 700;
}

.dialog-caption {
  color: #73808a;
  font-size: 12px;
}

.dialog-actions {
  background: rgba(255, 255, 255, 0.76);
  border-top: 1px solid rgba(210, 217, 222, 0.7);
  padding: 14px 20px;
}

@media (max-width: 600px) {
  .incentive-dialog-card {
    border-radius: 0 !important;
    min-height: 100vh;
  }

  .incentive-summary__body {
    align-items: flex-start;
  }
}
</style>

<style>
.incentive-dialog {
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(15, 23, 32, 0.28);
}

.incentive-dialog .v-btn {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.incentive-dialog .v-btn:not(.v-btn--icon) {
  border-radius: 24px;
  min-width: 104px;
  padding-left: 24px;
  padding-right: 24px;
  box-shadow: 0 8px 18px rgba(38, 50, 56, 0.18) !important;
}

.incentive-dialog .v-btn.primary:not(.v-btn--disabled) {
  box-shadow: 0 8px 18px rgba(253, 223, 0, 0.38) !important;
}

.incentive-dialog .v-btn.red:not(.v-btn--disabled),
.incentive-dialog .v-btn.error:not(.v-btn--disabled) {
  box-shadow: 0 8px 18px rgba(244, 67, 54, 0.32) !important;
}

@media (max-width: 600px) {
  .incentive-dialog {
    border-radius: 0;
    height: 100vh;
    margin: 0;
    max-height: none;
    max-width: none;
    width: 100%;
  }
}
</style>
